import React, { useCallback, useState } from "react";
import { z } from "zod";
import { api, extractApiError } from "@/lib/api";
import { useT, useLocalized } from "@/lib/LanguageContext";
import { toast } from "sonner";
import { Plus, Trash2, Edit3, Save, X, Loader2, Image as ImageIcon } from "lucide-react";
// RELATIVE paths on purpose (not "@/…"): these modules must be REALLY resolved under
// Jest, not mocked — the "@/" alias is not mapped on the Jest side (cf.
// lib/LanguageContext.jsx). A relative path resolves natively.
import TaxCategoryField from "./TaxCategoryField";
import LanguageAwareField from "./LanguageAwareField";
import useTaxProfile from "../hooks/useTaxProfile";
import useFieldErrors from "../hooks/useFieldErrors";
import { withAnyLangRule } from "../lib/validation";
import FieldError, { invalidInputClass } from "../components/ui/FieldError";
import { cn } from "../lib/utils";
import { fmtMoney } from "./money";
import useTenantCurrency from "../lib/useTenantCurrency";
import CatalogueCurrencyField, { isLegacyCurrency } from "./CatalogueCurrencyField";
import useAdminLoad, { payloadShapeError } from "../hooks/useAdminLoad";
// crm-module BEGIN C3.02/C3.04 (lane F4)
import PromoteButton from "./crm/promote/PromoteButton";
// crm-module END
import LoadFailure from "../components/ui/LoadFailure";
import { apiErrorCode } from "../lib/apiErrors";
import { adminHref } from "./adminPath";
import { useEntitlements } from "./entitlements/EntitlementsContext";
import { CAP, isAtLimit } from "./entitlements/capabilities";

// The server's refusal of a create past the plan's product ceiling
// (docs/architecture/plan-entitlements-contract.md, "Wire codes").
const PLAN_LIMIT_REACHED = "PLAN_LIMIT_REACHED";

// Both mirror server.py's write boundary (ProductIn): CATALOGUE_NAME_MAX = 200,
// CATALOGUE_PRICE_MAX_CENTS = 10_000_000. A form laxer than the API only moves the
// rejection to an opaque 422.
const NAME_MAX = 200;
const PRICE_MAX_CENTS = 10_000_000;

/**
 * What the API will and will not accept, stated where the practitioner can act on it.
 *
 * The name is `withAnyLangRule`, not `frRequiredField`: BUG-003's RE-OPEN was that
 * `ProductIn.name_fr` was `min_length=1`, so a practitioner running an English dashboard
 * could not save a product at all without inventing a French name — refused over a value
 * sitting on a tab they were not looking at. The rule is now the pair rule on BOTH sides
 * (`server._require_a_name_in_some_language`): at least one language, neither one
 * specifically. One is enough because every reader falls back — `pickLocalized` on the
 * frontend, `platform_helpers.catalogue_name` on the server, which is what names the
 * Stripe line item. A product with NO name in any language stays refused: that is the
 * original BUG-003 and it is a blank card in the shop.
 *
 * The price rule is a superRefine because it reads TWO fields: `_price_dollars` is free
 * text (parsing is part of the rule — "" and "abc" are not 0, they are unstated), and it
 * only applies to a `stripe` product. An affiliate product has NO price input at all —
 * its money lives on the merchant's site — so judging its price would refuse a save
 * over a field that is not on screen.
 *
 * The `type !== "affiliate"` gate (rather than `=== "stripe"`) mirrors server.py's
 * `ProductIn.type: Literal["stripe", "affiliate"] = "stripe"` +
 * `_positive_price_when_sellable`: the backend defaults an absent/None `type` to
 * "stripe" before checking it, so a payload with no `type` at all is still sellable and
 * still needs a positive price. `=== "stripe"` would let an absent type slip past the
 * client while the server rejects it with an opaque 422.
 *
 * The affiliate branch has its OWN rule, mirroring server.py's
 * `_affiliate_url_when_affiliate`: an affiliate product's money lives entirely on
 * `affiliate_url` (the storefront's only read for it, Boutique.jsx's `buy()`), so an
 * empty or non-http(s) value is refused here too, on the one field that IS on screen for
 * that type — rather than saving a row whose Buy button opens a blank tab.
 *
 * `.optional()` on the non-text fields is not laxity: a MISSING key would abort the
 * object parse, and an aborted parse reports zod's own `invalid_type` on a field no one
 * renders — a save refused with nothing highlighted, the very defect being fixed here. An
 * absent price is judged by the rule below (NaN → "enter a price"), on a visible field.
 */
const isAbsoluteHttpUrl = (value) => {
  try {
    const u = new URL(value);
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
};

// Exported (not just module-scope) so the absent-`type` mirror above can be pinned by a
// direct schema test: the UI itself always populates `form.type` (the `empty` template
// below defaults it to "stripe", and the <select> only ever emits "stripe"/"affiliate"),
// so an absent-type payload can only be exercised at the schema level, never by driving
// the rendered form.
export const productSchema = withAnyLangRule(z.object({
  name_fr: z.string().trim().max(NAME_MAX, "validation.tooLong").optional(),
  name_en: z.string().trim().max(NAME_MAX, "validation.tooLong").optional(),
  type: z.string().optional(),
  _price_dollars: z.union([z.string(), z.number()]).optional(),
  affiliate_url: z.string().optional(),
}), "name_fr", "name_en").superRefine((form, ctx) => {
  if (form.type === "affiliate") {
    if (!isAbsoluteHttpUrl((form.affiliate_url || "").trim())) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["affiliate_url"], message: "catalogue.validation.affiliateUrlRequired" });
    }
    return;
  }
  const cents = Math.round(Number(form._price_dollars) * 100);
  const message = !Number.isFinite(cents) || cents <= 0
    ? "catalogue.validation.pricePositive"
    : cents > PRICE_MAX_CENTS ? "catalogue.validation.priceMax" : null;
  if (message) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["_price_dollars"], message });
});

const empty = {
  name_fr: "", name_en: "", description_fr: "", description_en: "",
  // NO `currency`: the form never showed a currency input, and the hardcoded "cad" it
  // sent denominated every product in a currency the platform may not settle in — which
  // the money core refuses, so the sale could be charged and never booked. The server
  // denominates a new row in the platform's settlement currency and, on an edit, keeps
  // whatever the row already had.
  price_cents: 0, _price_dollars: "0.00", image_url: "",
  type: "stripe", product_type: "physical", affiliate_url: "", in_stock: true,
  active: true, order: 0,
  tax_category: "standard", exemption_citation: "", sac_code: "",
};

export default function ProductsAdmin() {
  const t = useT("admin");
  const tenantCurrency = useTenantCurrency();
  const tv = useT("common");
  const loc = useLocalized();
  const taxProfile = useTaxProfile();
  const { fieldErrors, validate, clearFieldError, clearAllErrors } = useFieldErrors();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [shopEnabled, setShopEnabled] = useState(true);
  const ent = useEntitlements();
  // The ceiling comes from the plan; the count is the list on screen — every product row
  // of the practice, active or not, which is exactly what the server counts — so a
  // create or a delete moves the button without waiting for a refresh of the map. After
  // a failed read the count is unknown and nothing is claimed (the server still refuses).
  const productCeiling = ent.limit(CAP.productsLimit)?.value;

  // A field's error is a statement about its value AT SUBMIT TIME: it must not outlive
  // the next keystroke in that field. Only the validated fields go through this — the
  // others keep their plain setForm.
  const updateField = (k, v) => { setForm((f) => ({ ...f, [k]: v })); clearFieldError(k); };
  // One input, two languages, ONE message slot — the pair rule is reported on `name_fr`
  // by withAnyLangRule while a per-language ceiling is reported on whichever language
  // broke it, and both must reach the single field on screen. A keystroke in either
  // language retires both: either one can be what satisfies the pair. Same shape as
  // TestimonialsAdmin's `quoteError` / `updateQuote`.
  const nameError = fieldErrors.name_fr || fieldErrors.name_en;
  const updateName = (lang, v) => {
    setForm((f) => ({ ...f, [`name_${lang}`]: v }));
    clearFieldError("name_fr");
    clearFieldError("name_en");
  };
  const priceCeiling = (PRICE_MAX_CENTS / 100).toFixed(2);

  // Stable identity: `useAdminLoad` uses it as its effect dependency, and save()/remove()
  // below share the very same reader to refresh the list.
  const load = useCallback(async () => {
    const [{ data }, settings] = await Promise.all([
      api.get("/admin/products"),
      api.get("/admin/settings"),
    ]);
    const config = settings.data;
    if (!Array.isArray(data) || !config || typeof config !== "object") throw payloadShapeError();
    setItems(data);
    setShopEnabled(!!config.shop_enabled);
  }, []);
  // BUG-051 — a failed read is NOT an empty shop. Without this, ANY failure of either
  // request left `items` at `[]` and `shopEnabled` at its initial value, and the screen
  // said "No products yet" beside a public-shop switch nobody had read the state of.
  // Both facts are unknown after a failed read, so neither is stated: the list AND the
  // switch give way to the notice below.
  const { loadError, reload } = useAdminLoad(load);
  const atProductLimit = !loadError && isAtLimit(productCeiling, items.length);

  const toggleShop = async () => {
    const newVal = !shopEnabled;
    try {
      await api.post("/admin/settings", { shop_enabled: newVal });
      setShopEnabled(newVal);
    } catch (e) {
      // The checkbox is NOT moved on failure: the public shop is still in its old state,
      // and a toggle that flipped anyway would misreport what visitors can see.
      toast.error(extractApiError(e, tv("errors.saveFailed"), tv));
    }
  };

  const save = async () => {
    if (validate(productSchema, form)) { toast.error(tv("validation.fixHighlighted")); return; }
    setBusy(true);
    // `price_cents` is NEVER bound straight to an input: the practitioner types DOLLARS
    // (`_price_dollars`), converted to cents here — otherwise "90" typed at the
    // keyboard was stored as 0.90 $ (a real bug, cf. admin/money.js `toCents`).
    const { _price_dollars, ...rest } = form;
    const payload = {
      ...rest,
      price_cents: Math.round(Number(_price_dollars || 0) * 100),
      order: parseInt(form.order || 0, 10),
    };
    try {
      if (editing === "new") await api.post("/admin/products", payload);
      else await api.put(`/admin/products/${editing.id}`, payload);
    } catch (e) {
      toast.error(extractApiError(e, tv("errors.saveFailed"), tv));
      // The ceiling on screen was stale (e.g. the plan changed since the console opened):
      // re-read it so the "New product" button states the limit the server applies.
      if (apiErrorCode(e) === PLAN_LIMIT_REACHED) ent.reload();
      setBusy(false);
      return;
    }
    // The write above already succeeded — closing the editor here, not after the reload
    // below, is what keeps a failed reload from reading as a failed save: leaving the
    // editor open on that failure is what invited a retry that duplicated the row.
    setEditing(null);
    try {
      await load();
    } catch (e) {
      toast.error(extractApiError(e, t("toast.reloadFailed"), tv));
    } finally {
      setBusy(false);
    }
  };

  const uploadImage = async (file) => {
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/media", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const fullUrl = data.url.startsWith("http") ? data.url : `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      setForm((f) => ({ ...f, image_url: fullUrl }));
    } catch (e) {
      toast.error(extractApiError(e, t("media.toast.uploadFailed"), tv));
    }
  };

  const remove = async (p) => {
    if (!window.confirm(t("shop.confirmDelete"))) return;
    try {
      await api.delete(`/admin/products/${p.id}`);
    } catch (e) {
      toast.error(extractApiError(e, t("toast.deleteFailed"), tv));
      return;
    }
    // The delete above already succeeded — a reload failure here is a stale LIST, not a
    // failed delete, and must not be reported as one.
    try {
      await load();
    } catch (e) {
      toast.error(extractApiError(e, t("toast.reloadFailed"), tv));
    }
  };

  return (
    <div data-testid="admin-products">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-4xl">{t("shop.title")}</h1>
          <p className="text-eh-muted mt-1">{t("shop.subtitle")}</p>
        </div>
        <div className="flex items-center gap-3">
          {/* The switch is hidden, not merely stale, while the read is failing: its
              handler writes `!shopEnabled`, and `shopEnabled` was never read — clicking
              it could take the public shop OFFLINE while the practitioner believes they
              are putting it online. Creating a product stays available; only the claims
              about server state are withdrawn. */}
          {!loadError && (
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={shopEnabled} onChange={toggleShop} className="w-4 h-4 accent-eh-sage-deep" data-testid="shop-enabled-toggle" />
              {t("shop.activePublic")}
            </label>
          )}
          {!editing && (
            <button
              onClick={() => { setForm(empty); clearAllErrors(); setEditing("new"); }}
              disabled={atProductLimit}
              aria-describedby={atProductLimit ? "product-limit-reached" : undefined}
              className="btn-primary text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="product-new"
            >
              <Plus size={14} /> {t("shop.new")}
            </button>
          )}
        </div>
      </div>

      {atProductLimit && !editing && (
        <div
          id="product-limit-reached"
          data-testid="product-limit-reached"
          className="mt-6 bg-amber-50 border border-amber-200 rounded-2xl p-4 text-sm text-amber-900 flex items-center justify-between gap-4 flex-wrap"
        >
          <span>{t("shop.limitReached", { limit: productCeiling })}</span>
          <a href={adminHref("/admin/billing")} className="font-semibold underline whitespace-nowrap" data-testid="product-limit-billing">
            {t("ent.notInPlan.cta")}
          </a>
        </div>
      )}

      <LoadFailure
        message={loadError ? extractApiError(loadError, tv("errors.loadFailed"), tv) : ""}
        onRetry={reload}
        retryLabel={tv("actions.retry")}
        testId="admin-products-load-error"
        retryTestId="admin-products-retry"
        className="mt-8"
      />

      {!editing && !loadError && (
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.length === 0 && <div className="col-span-full text-eh-muted text-sm bg-eh-cream border border-eh-border-soft rounded-2xl p-8 text-center">{t("shop.empty")}</div>}
          {items.map((p) => (
            <div key={p.id} className="bg-eh-cream border border-eh-border-soft rounded-2xl p-4 flex gap-4">
              <div className="w-20 h-20 rounded-xl bg-eh-bg overflow-hidden shrink-0 flex items-center justify-center">
                {p.image_url ? <img src={p.image_url} alt="" className="w-full h-full object-cover" /> : <ImageIcon size={18} className="text-eh-muted" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 text-xs">
                  <span className={`px-2 py-0.5 rounded-full ${p.active ? "bg-eh-sage/30 text-eh-sage-deep" : "bg-eh-border-soft text-eh-muted"}`}>{p.active ? t("shop.active") : t("shop.inactive")}</span>
                  <span className="text-eh-muted">{p.type === "affiliate" ? t("shop.affiliate") : "Stripe"}</span>
                  {p.type === "stripe" && isLegacyCurrency(p.currency, tenantCurrency) && (
                    <span className="px-2 py-0.5 rounded-full bg-red-50 text-red-700" data-testid={`product-legacy-currency-${p.id}`}>
                      {t("catalogue.currency.notPayable", { held: p.currency.toUpperCase() })}
                    </span>
                  )}
                </div>
                <div className="font-display text-lg mt-1 truncate">{loc(p, "name")}</div>
                <div className="text-sm text-eh-muted">{p.type === "stripe" ? fmtMoney(p.price_cents, p.currency) : t("shop.externalLink")}</div>
              </div>
              <div className="flex flex-col gap-1">
                <button onClick={() => { setForm({ ...empty, ...p, _price_dollars: ((p.price_cents || 0) / 100).toFixed(2) }); clearAllErrors(); setEditing(p); }} className="p-2 rounded-full hover:bg-white" data-testid={`product-edit-${p.id}`}><Edit3 size={14} /></button>
                <button onClick={() => remove(p)} className="p-2 rounded-full text-red-600 hover:bg-red-50"><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="mt-6 bg-eh-cream border border-eh-border-soft rounded-3xl p-7" data-testid="product-editor">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-3">
              <h2 className="font-display text-2xl">{editing === "new" ? t("shop.newProduct") : t("shop.edit")}</h2>
              {/* crm-module BEGIN C3.02/C3.04 — Promote (lane F4). Hidden for a new,
                  unsaved product (crm-frontend-design.md §B.4). */}
              {editing !== "new" && (
                <PromoteButton itemType="product" itemId={editing.id} itemName={loc(editing, "name")} thumbnailUrl={editing.image_url} />
              )}
              {/* crm-module END */}
            </div>
            <div className="flex gap-2">
              <button onClick={() => setEditing(null)} className="btn-ghost text-sm"><X size={14} /> {t("shop.cancel")}</button>
              <button onClick={save} disabled={busy} className="btn-primary text-sm" data-testid="product-save">{busy ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} {t("shop.save")}</button>
            </div>
          </div>
          <div className="space-y-4 mb-4">
            <LanguageAwareField
              label={t("shop.nameFr") ? t("shop.nameFr").replace(" (FR)", "").replace(" (EN)", "") : "Product Name"}
              valueFr={form.name_fr}
              valueEn={form.name_en}
              onChangeFr={(v) => updateName("fr", v)}
              onChangeEn={(v) => updateName("en", v)}
              requiredLang="any"
              error={nameError ? tv(nameError) : ""}
              testid="product-name"
            />
            <LanguageAwareField
              label={t("shop.descFr") ? t("shop.descFr").replace(" (FR)", "").replace(" (EN)", "") : "Description"}
              valueFr={form.description_fr}
              valueEn={form.description_en}
              onChangeFr={(v) => setForm({ ...form, description_fr: v })}
              onChangeEn={(v) => setForm({ ...form, description_en: v })}
              multiline
              rows={3}
              testid="product-description"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase tracking-[0.18em] text-eh-muted">{t("shop.type")}</label>
              {/* Switching type retires whichever rule belongs to the field that is about
                  to leave the screen (price for affiliate, affiliate_url for stripe) —
                  the rule itself does not apply there. */}
              <select value={form.type} onChange={(e) => { setForm({ ...form, type: e.target.value }); clearFieldError("_price_dollars"); clearFieldError("affiliate_url"); }} className="mt-1 w-full bg-white border border-eh-border-soft rounded-xl px-3 py-2.5 text-sm" data-testid="product-type">
                <option value="stripe">{t("shop.typeStripe")}</option>
                <option value="affiliate">{t("shop.typeAffiliate")}</option>
              </select>
            </div>
            {form.type === "stripe" ? (
              <>
                {/* Physical or digital. Not cosmetic and not new: the payment gateway
                    resolves the platform's commission per product nature, and until this
                    field existed every shop item was priced as "digital" because the
                    backend model never declared one for the gateway to read. Only a
                    `stripe` product is ever sold by us, so an affiliate row has no say. */}
                <div>
                  <label className="text-xs uppercase tracking-[0.18em] text-eh-muted">{t("shop.productType")}</label>
                  <select
                    value={form.product_type}
                    onChange={(e) => setForm({ ...form, product_type: e.target.value })}
                    className="mt-1 w-full bg-white border border-eh-border-soft rounded-xl px-3 py-2.5 text-sm"
                    data-testid="product-nature"
                  >
                    <option value="physical">{t("shop.productTypePhysical")}</option>
                    <option value="digital">{t("shop.productTypeDigital")}</option>
                  </select>
                </div>
                <Inp
                  label={t("shop.price")}
                  value={form._price_dollars}
                  onChange={(v) => updateField("_price_dollars", v)}
                  type="number"
                  step="0.01"
                  testid="product-price"
                  error={fieldErrors._price_dollars ? t(fieldErrors._price_dollars, { max: priceCeiling }) : ""}
                />
                {/* STATED, not chosen; switchable only for a row held in a retired
                    currency. See CatalogueCurrencyField. */}
                <CatalogueCurrencyField
                  currency={form.currency}
                  denomination={tenantCurrency}
                  onSwitch={(cur) => setForm((f) => ({ ...f, currency: cur }))}
                  label={t("shop.currency")}
                  testid="product"
                />
                <TaxCategoryField
                  value={{ tax_category: form.tax_category, exemption_citation: form.exemption_citation, sac_code: form.sac_code }}
                  onChange={(next) => setForm({ ...form, ...next })}
                  country={taxProfile?.country}
                />
              </>
            ) : (
              <Inp
                label={t("shop.affiliateUrl")}
                value={form.affiliate_url}
                onChange={(v) => updateField("affiliate_url", v)}
                testid="product-affiliate-url"
                error={fieldErrors.affiliate_url ? t(fieldErrors.affiliate_url) : ""}
              />
            )}
            <div>
              <label className="text-xs uppercase tracking-[0.18em] text-eh-muted">{t("shop.image")}</label>
              <div className="mt-1 flex items-center gap-3">
                {form.image_url ? <img src={form.image_url} alt="" className="w-14 h-14 rounded-lg object-cover" /> : <div className="w-14 h-14 rounded-lg bg-white border border-eh-border-soft flex items-center justify-center"><ImageIcon size={14} className="text-eh-muted" /></div>}
                <label className="btn-ghost text-xs cursor-pointer">{t("shop.upload")}<input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && uploadImage(e.target.files[0])} /></label>
              </div>
            </div>
            <Inp label={t("shop.order")} value={form.order} onChange={(v) => setForm({ ...form, order: v })} type="number" />
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="w-4 h-4 accent-eh-sage-deep" /> {t("shop.active")}
            </label>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * `error` is the message ALREADY RESOLVED by the caller's `t` (state holds an i18n key —
 * see lib/validation.js). It drives both the border and the message under the input, so a
 * long form can be scanned for the offending field without reading every label.
 */
const Inp = ({ label, value, onChange, type = "text", multiline, step, testid, error }) => (
  <div className={multiline ? "md:col-span-2" : ""}>
    <label className="text-xs uppercase tracking-[0.18em] text-eh-muted">{label}</label>
    {multiline ? (
      <textarea rows={3} value={value} onChange={(e) => onChange(e.target.value)} aria-invalid={error ? true : undefined} className={cn("mt-1 w-full bg-white border border-eh-border-soft rounded-xl px-3 py-2.5 text-sm resize-none", error && invalidInputClass)} data-testid={testid} />
    ) : (
      <input type={type} step={step} value={value} onChange={(e) => onChange(e.target.value)} aria-invalid={error ? true : undefined} className={cn("mt-1 w-full bg-white border border-eh-border-soft rounded-xl px-3 py-2.5 text-sm", error && invalidInputClass)} data-testid={testid} />
    )}
    <FieldError message={error} testId={testid ? `${testid}-error` : undefined} />
  </div>
);
